// src/registerServiceWorker.js
//
// Registers public/sw.js so the app opens with no signal. Web only — the
// installed Capacitor app already ships its own bundled build and serves it from
// the filesystem, so a service worker there would just add a second, competing
// cache layer.
//
// The main hazard with service workers is a bad one pinning users to a stale
// build forever. Guards against that:
//   * sw.js calls skipWaiting()/clients.claim(), so a new worker takes over at
//     once instead of waiting for every tab to close.
//   * activate deletes every cache whose version key doesn't match.
//   * unregisterServiceWorker() below is the escape hatch if it ever misbehaves.

export function registerServiceWorker() {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  // Native app bundles its own build (capacitor.config webDir: 'build').
  if (window.Capacitor?.isNativePlatform?.()) return;
  // CRA dev server does its own hot reloading; a worker in the way confuses it.
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') return;

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            // A new build is ready and an old one is still controlling the page.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              installing.postMessage('SKIP_WAITING');
            }
          });
        });
      })
      .catch((err) => {
        // Never fatal — the app works fine without offline support.
        console.warn('Service worker registration failed:', err);
      });

    // When the new worker takes over, reload once so the crew is on the new build.
    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });
  });
}

/** Escape hatch: rip out the worker and every cache it owns. */
export async function unregisterServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((r) => r.unregister()));
  if (window.caches) {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }
}
